/**
 * GLAMRDiP colours grid interactions
 * - Desktop ≥990: filter checkboxes + sort dropdown auto-submit on change
 *   (debounced 200ms so multi-clicks are coalesced).
 * - Mobile <990: filter drawer slides in from left, body scroll-locked,
 *   backdrop click closes, Esc closes. Form submits via the explicit
 *   "Apply" button (no auto-submit on mobile so users can multi-select
 *   before reload).
 *
 * Submit strategy: build URLSearchParams from checked inputs + price
 * range + sort. Set window.location.search directly so URL params persist
 * (Shopify's storefront filters work entirely off URL params — no AJAX).
 */
(() => {
  'use strict';
  const form     = document.querySelector('[data-gd-colours-filters]');
  const drawer   = document.querySelector('[data-gd-filters-drawer]');
  const backdrop = document.querySelector('[data-gd-filters-backdrop]');
  // Joel 2026-05-07: querySelectorAll (was querySelector) — the snippet
  // renders TWO triggers when use_top_filter_bar is enabled:
  //   - .gd-colours__mobile-trigger (mobile-only, display: none ≥990px)
  //   - .gd-colours__top-filter-btn (full-width desktop top bar)
  // The previous singular query bound the click handler ONLY to the
  // first DOM match (mobile-trigger), so on desktop full-width mode
  // the visible top button had no listener → click did nothing →
  // filter drawer never opened.
  const openBtns = document.querySelectorAll('[data-gd-filters-open]');
  const openBtn  = openBtns[0] || null;   // first reference for focus restoration on close
  const closeBtns = document.querySelectorAll('[data-gd-filters-close]');
  const sort     = document.querySelector('[data-gd-sort]');
  if (!form && !sort && !drawer) return;

  const MOBILE_MQ = window.matchMedia ? window.matchMedia('(max-width: 989px)') : { matches: false };

  // Joel 2026-05-02: top-filter-bar mode (colours-grid section setting
  // `use_top_filter_bar`). When the root has `gd-colours--top-filter`,
  // the drawer is the ONLY way to apply filters even on desktop (no
  // sidebar) so we MUST disable the desktop auto-submit — otherwise
  // ticking the first checkbox immediately submits + reloads, killing
  // the multi-select UX inside the drawer. Apply button submits.
  const TOP_FILTER_MODE = !!document.querySelector('.gd-colours--top-filter');

  const buildSubmitUrl = () => {
    const params = new URLSearchParams();
    if (form) {
      form.querySelectorAll('input[type="checkbox"]:checked').forEach((cb) => {
        params.append(cb.name, cb.value);
      });
      // Price-range number inputs aren't checkboxes — append non-empty values.
      form.querySelectorAll('input[type="number"]').forEach((inp) => {
        if (inp.name && inp.value !== '') params.set(inp.name, inp.value);
      });
    }
    // Sort: on mobile, prefer the in-form radio (drawer drives sort
    // there). On desktop, prefer the toolbar select (drawer's sort
    // <details> is CSS-hidden so the radio holds the original
    // server-rendered value, not the user's intent). Joel 2026-05-01
    // #153: unified filter+sort drawer adds form radios for sort_by.
    const formSortRadio = form && form.querySelector('input[type="radio"][name="sort_by"]:checked');
    if (MOBILE_MQ.matches && formSortRadio) {
      params.set('sort_by', formSortRadio.value);
    } else if (sort && sort.value) {
      params.set('sort_by', sort.value);
    } else if (formSortRadio) {
      params.set('sort_by', formSortRadio.value);
    }
    const url = new URL(window.location.href);
    url.search = params.toString();
    return url.toString();
  };

  const submit = () => { window.location.href = buildSubmitUrl(); };

  // Desktop auto-submit (debounced). On mobile rely on the Apply button.
  // In top-filter-bar mode, also rely on the Apply button on desktop —
  // the drawer is the only filter UI and auto-submit would close it
  // mid-multi-select.
  let debounceTimer = null;
  const submitDebounced = () => {
    if (MOBILE_MQ.matches || TOP_FILTER_MODE) return;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(submit, 200);
  };
  form?.addEventListener('change', submitDebounced);
  sort?.addEventListener('change', submit);

  // Mobile Apply button — explicit submit. Intercept the form's native
  // submit so we use the same buildSubmitUrl path.
  form?.addEventListener('submit', (e) => {
    e.preventDefault();
    submit();
  });

  // ────── Live count on the Apply button (Claude 2026-08-05) ──────
  // The button label is server-rendered from grid_collection.products_count,
  // so it always reported the CURRENTLY loaded page's count — tick a filter
  // and it still said "Vis 32 farger" while the drawer showed "På lager 31".
  // Nothing in the theme ever updated it.
  //
  // Rather than recompute the count client-side (impossible to do correctly
  // across several filter groups, which Shopify ANDs together), we ask
  // Shopify: fetch the prospective URL through the Section Rendering API and
  // copy the freshly rendered button's own text. That keeps pluralisation and
  // per-market wording exactly as Liquid produced them, with no duplication
  // of label logic here.
  //
  // Only runs where the Apply button is the way filters get applied (mobile,
  // or top-filter-bar mode). In desktop sidebar mode a change auto-submits
  // 200ms later, so a count fetch would be wasted work.
  const applyBtn  = form ? form.querySelector('.gd-colours__filters-apply') : null;
  const sectionEl = form ? form.closest('.shopify-section') : null;
  const sectionId = sectionEl ? sectionEl.id.replace(/^shopify-section-/, '') : null;
  let countTimer = null;
  let countAbort = null;

  const refreshApplyCount = () => {
    if (!applyBtn) return;
    if (!(MOBILE_MQ.matches || TOP_FILTER_MODE)) return;
    clearTimeout(countTimer);
    countTimer = setTimeout(() => {
      const target = new URL(buildSubmitUrl());
      if (sectionId) target.searchParams.set('section_id', sectionId);
      if (countAbort) countAbort.abort();
      countAbort = new AbortController();
      applyBtn.setAttribute('aria-busy', 'true');
      fetch(target.toString(), { credentials: 'same-origin', signal: countAbort.signal })
        .then((r) => (r.ok ? r.text() : null))
        .then((html) => {
          if (!html) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('.gd-colours__filters-apply');
          const txt = fresh && fresh.textContent.trim();
          // Empty/garbled response → keep whatever the button already says.
          if (txt) applyBtn.textContent = txt;
        })
        .catch(() => {})
        .finally(() => applyBtn.removeAttribute('aria-busy'));
    }, 250);
  };
  form?.addEventListener('change', refreshApplyCount);

  // ────── Drawer (mobile) ──────
  const openDrawer = () => {
    if (!drawer) return;
    drawer.classList.add('is-open');
    backdrop?.classList.add('is-open');
    // Joel 2026-04-30: shared iOS-safe scroll-lock instead of naive
    // body.overflow:hidden. Stops scroll-chain from the filter drawer
    // body to the page underneath on iOS Safari.
    if (window.gdScrollLock) window.gdScrollLock.lock(drawer);
    else document.body.style.overflow = 'hidden';
    const firstFocusable = drawer.querySelector('button, a, input:not([type=hidden])');
    firstFocusable?.focus();
  };
  const closeDrawer = () => {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    backdrop?.classList.remove('is-open');
    if (window.gdScrollLock) window.gdScrollLock.unlock(drawer);
    else document.body.style.overflow = '';
    openBtn?.focus();
  };

  openBtns.forEach((b) => b.addEventListener('click', openDrawer));
  closeBtns.forEach((b) => b.addEventListener('click', closeDrawer));
  backdrop?.addEventListener('click', closeDrawer);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer?.classList.contains('is-open')) closeDrawer();
  });

  // ────── Filter state restoration (Joel 2026-05-07) ──────
  // Server-side Liquid already renders the form's checkboxes with
  // `checked` based on URL params (gd-product-grid.liquid line ~149:
  // `{% if value.active %}checked{% endif %}`). So a fresh fetch with
  // a filter URL produces the correct HTML — verified via curl.
  //
  // This restoration handler is defensive belt-and-braces for two
  // edge cases:
  //   1. bfcache restore (browser back from PDP) — `pageshow` event
  //      fires with `e.persisted === true`. The page is restored
  //      from memory exactly as it was at unload, so checkboxes
  //      should already be in the correct state. But if any other
  //      script has mutated state since (e.g. an app embed), this
  //      re-syncs to URL truth.
  //   2. Hydration mismatch — if any client-side script clears
  //      checkbox state during DOMContentLoaded (race condition with
  //      app embeds), this re-applies the URL truth after.
  //
  // No auto-submit: the function ONLY toggles checkbox state; no
  // form submission, no URL change, no grid re-render. Server-
  // rendered grid is already correct for the URL.
  function restoreFilterStateFromURL() {
    if (!form) return;
    const params = new URLSearchParams(window.location.search);
    let changed = false;
    for (const [key, value] of params.entries()) {
      if (!key.startsWith('filter.')) continue;
      // CSS.escape guards against filter param names that may contain
      // dots / special chars (e.g. `filter.v.option.colour_family`).
      try {
        const cb = form.querySelector(
          `input[type="checkbox"][name="${CSS.escape(key)}"][value="${CSS.escape(value)}"]`
        );
        if (cb && !cb.checked) {
          cb.checked = true;
          changed = true;
        }
      } catch {}
    }
    // Sort radio: same restore for the form's radio + the toolbar
    // <select> (whichever is present in the current layout).
    const sortValue = params.get('sort_by');
    if (sortValue) {
      const radio = form.querySelector(
        `input[type="radio"][name="sort_by"][value="${CSS.escape(sortValue)}"]`
      );
      if (radio && !radio.checked) { radio.checked = true; changed = true; }
      if (sort && sort.value !== sortValue) { sort.value = sortValue; changed = true; }
    }
    return changed;
  }

  // Run once on DOMContentLoaded (covers initial load + standard
  // navigation). Also run on `pageshow` with persisted=true (bfcache
  // restore — covers the browser-back-from-PDP scenario).
  restoreFilterStateFromURL();
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) restoreFilterStateFromURL();
  });
})();

// ────── Colour chips: swap the grid in place ────────────────────────────────
//
// The chips are ordinary <a> links to Shopify's own filter URLs. Everything here
// is an ENHANCEMENT on top of that: intercept the tap, fetch the same URL via the
// Section Rendering API, swap in the freshly rendered pieces. Any failure —
// offline, a 5xx, an unrecognised response — falls through to a normal
// navigation, which is exactly how this page filters today. Nothing below can
// leave the shopper worse off than before the chips existed.
//
// WHAT IS AND IS NOT REPLACED, because this took reading the neighbours to get right:
//   · The filter DRAWER is never replaced. gd-colours-grid.js above is a one-shot
//     IIFE that binds its open/close buttons directly and has no re-init hook, so
//     replacing the drawer would leave it dead. The filter OPTION LISTS inside the
//     form are refreshed, which is safe because `change` is delegated to the form.
//   · The infinite-scroll root is not replaced either — the drawer lives inside it.
//     Instead the grid, the sentinel foot and data-gd-infinite-next are updated and
//     gd-infinite-scroll.js is re-initialised. That file caches nextUrl in a closure
//     and observes the sentinel captured at bind time, so without the re-init a chip
//     tap would leave it paging the PREVIOUS filter's results.
//   · Re-init happens by clearing its own data-gd-infinite-init guard and firing
//     shopify:section:load — the theme's existing convention, which every script
//     here already has to survive because the customizer fires it routinely.
(function gdColourChipSwap() {
  'use strict';

  var chipNav = document.querySelector('[data-gd-colour-chips]');
  if (!chipNav) return;                                  // section did not opt in
  var sectionEl = chipNav.closest('.shopify-section');
  var sectionId = sectionEl ? sectionEl.id.replace(/^shopify-section-/, '') : null;
  // No section id, no DOMParser, no AbortController → leave the links alone.
  if (!sectionId || !window.DOMParser || !window.AbortController) return;

  var abort = null;

  function replaceNode(sel, doc) {
    var cur = document.querySelector(sel);
    var next = doc.querySelector(sel);
    if (cur && next) { cur.replaceWith(next); return next; }
    if (cur && !next) { cur.remove(); }
    return null;
  }
  function copyText(sel, doc) {
    var a = document.querySelector(sel), b = doc.querySelector(sel);
    if (a && b) a.textContent = b.textContent;
  }

  function swap(url, push) {
    var grid = document.querySelector('[data-gd-infinite-grid]');
    var target;
    try { target = new URL(url, window.location.origin); }
    catch (e) { window.location.assign(url); return; }
    if (!grid) { window.location.assign(url); return; }
    target.searchParams.set('section_id', sectionId);

    if (abort) abort.abort();
    abort = new AbortController();
    grid.classList.add('is-gd-swapping');
    chipNav.setAttribute('aria-busy', 'true');

    fetch(target.toString(), { credentials: 'same-origin', signal: abort.signal })
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) {
        if (!html) throw new Error('empty');
        var doc = new DOMParser().parseFromString(html, 'text/html');
        if (!doc.querySelector('[data-gd-infinite-grid]') || !doc.querySelector('[data-gd-colour-chips]')) {
          throw new Error('unrecognised');
        }

        replaceNode('[data-gd-infinite-grid]', doc);
        chipNav = replaceNode('[data-gd-colour-chips]', doc) || chipNav;
        replaceNode('.gd-colours__active-filters', doc);
        replaceNode('.gd-colours__infinite-foot', doc);
        copyText('.gd-colours__count', doc);
        copyText('.gd-colours__top-count', doc);
        // The drawer's apply CTA prints the result count ("Vis 48 farger") and
        // the toolbar button carries the active-filter pip. Neither element is
        // inside anything replaced above, so both would keep showing the count
        // from before the chip was tapped.
        copyText('.gd-colours__filters-apply', doc);
        var fbtn = document.querySelector('.gd-colours__top-filter-btn');
        var fbtnNew = doc.querySelector('.gd-colours__top-filter-btn');
        // innerHTML, not replaceWith: the open-drawer handler is bound to this
        // button node, so the node has to survive.
        if (fbtn && fbtnNew) fbtn.innerHTML = fbtnNew.innerHTML;

        // Drawer option lists — checkbox state and per-value counts. Safe to
        // replace: the change handler is bound to the form, not to the inputs.
        var curOpts = document.querySelectorAll('.gd-colours__filters .gd-colours__filter-options');
        var newOpts = doc.querySelectorAll('.gd-colours__filters .gd-colours__filter-options');
        if (curOpts.length && curOpts.length === newOpts.length) {
          for (var i = 0; i < curOpts.length; i++) curOpts[i].replaceWith(newOpts[i]);
        }

        // Re-point infinite scroll at the new result set.
        var root = document.querySelector('[data-gd-infinite-root]');
        var newRoot = doc.querySelector('[data-gd-infinite-root]');
        if (root && newRoot) {
          root.setAttribute('data-gd-infinite-next', newRoot.getAttribute('data-gd-infinite-next') || '');
          root.removeAttribute('data-gd-infinite-init');
          if (sectionEl) {
            sectionEl.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
          }
        }

        if (push) {
          var clean = new URL(url, window.location.origin);
          clean.searchParams.delete('section_id');
          window.history.pushState({ gdChip: 1 }, '', clean.toString());
        }

        // A new result set starts at the top; do not strand them halfway down
        // the previous one.
        var anchor = document.querySelector('[data-gd-colour-chips]');
        if (anchor) {
          var y = anchor.getBoundingClientRect().top + window.scrollY - 90;
          if (window.scrollY > y + 4) window.scrollTo({ top: y, behavior: 'smooth' });
        }
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;     // superseded by a newer tap
        window.location.assign(url);                      // plain link behaviour
      })
      .then(function () {
        var g = document.querySelector('[data-gd-infinite-grid]');
        if (g) g.classList.remove('is-gd-swapping');
        if (chipNav) chipNav.removeAttribute('aria-busy');
      });
  }

  document.addEventListener('click', function (e) {
    var chip = e.target && e.target.closest ? e.target.closest('[data-gd-chip]') : null;
    if (!chip) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;  // new tab stays a new tab
    var href = chip.getAttribute('href');
    if (!href) return;
    e.preventDefault();
    swap(href, true);
  });

  // The entry the shopper LANDED on was never pushed by us, so its history
  // state is null — and the popstate handler below ignores null states. Without
  // this, tapping a chip and then pressing Back put the URL back to the
  // unfiltered collection while the grid kept showing the filtered set: the
  // page lied about what it was displaying. Stamping the landing entry makes
  // Back a real un-filter. The existing state object is preserved rather than
  // replaced, so any other script keeping state on this entry keeps it.
  try {
    var st0 = history.state || {};
    if (!st0.gdChip) { st0.gdChip = 1; history.replaceState(st0, '', window.location.href); }
  } catch (e) { /* replaceState can throw in exotic sandboxes; chips still work */ }

  window.addEventListener('popstate', function (e) {
    if (!e.state || !e.state.gdChip) return;   // not one of ours; let the browser handle it
    swap(window.location.href, false);
  });
})();

/* ── Sort pill width — match the Instant Gels button ───────────────────────
   A native <select> is laid out as wide as its LONGEST option, so this pill
   always reserved room for "Alfabetisk, A-Å" whatever was actually selected.
   Next to the "Alle filtre" button that made it ~65px wider than the Instant
   Gels sort button it is supposed to mirror.

   The <select> is kept — it is the real control, it works with the keyboard,
   it works with JS off, and it carries the per-market sort labels. Only its
   width is corrected, by measuring the SELECTED option's text.

   `field-sizing: content` does exactly this in one CSS line, but it is not in
   every engine yet and this page takes heavy paid traffic on whatever browser
   the ad click arrives in. Measuring is exact everywhere.

   Everything is inside a try/catch: if any of it fails, the select falls back
   to its natural width, which is what shipped before this function existed —
   a slightly wide pill, never a broken or clipped control. */
(function gdSortPillWidth() {
  'use strict';

  var sel = document.querySelector('.gd-colours--chips .gd-colours__sort--top select');
  if (!sel) return;                       // not chips mode — nothing to size
  var ruler = null;

  function fit() {
    try {
      var opt = sel.options[sel.selectedIndex];
      if (!opt) return;
      if (!ruler) {
        ruler = document.createElement('span');
        ruler.setAttribute('aria-hidden', 'true');
        ruler.style.cssText =
          'position:absolute;top:-9999px;left:-9999px;visibility:hidden;white-space:pre';
        document.body.appendChild(ruler);
      }
      var cs = getComputedStyle(sel);
      ruler.style.fontFamily    = cs.fontFamily;
      ruler.style.fontSize      = cs.fontSize;
      ruler.style.fontWeight    = cs.fontWeight;
      ruler.style.fontStyle     = cs.fontStyle;
      ruler.style.letterSpacing = cs.letterSpacing;
      ruler.style.textTransform = cs.textTransform;
      ruler.textContent = opt.text;
      var text = ruler.getBoundingClientRect().width;
      if (!(text > 0)) return;             // hidden, zero-width font, bail
      // border-box: the text, plus the padding that holds the chevron, plus
      // 2px so a hinting difference can never clip the last glyph.
      var pad = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
      sel.style.width = Math.ceil(text + pad + 2) + 'px';
    } catch (e) { /* natural width is a fine fallback */ }
  }

  fit();
  // Changing the sort reloads the page, so this mostly matters for the moment
  // between the pick and the navigation — but it costs nothing to be correct.
  sel.addEventListener('change', fit);
  // The first measurement can land on the fallback font. Re-measure once the
  // real one is in, or the pill stays sized for the wrong typeface.
  if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
    document.fonts.ready.then(fit).catch(function () {});
  }
})();
